import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { birthdayConfig } from '../config';
import { assetUrl } from '../data/assets';

type YouTubeStateEvent = { data: number };
type YouTubeErrorEvent = { data: number };

type YouTubePlayer = {
  playVideo: () => void;
  pauseVideo: () => void;
  mute: () => void;
  unMute: () => void;
  setVolume: (volume: number) => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  destroy: () => void;
};

type YouTubeNamespace = {
  Player: new (
    elementId: string,
    options: {
      width: number;
      height: number;
      videoId: string;
      playerVars: Record<string, number | string>;
      events: {
        onReady: () => void;
        onStateChange: (event: YouTubeStateEvent) => void;
        onError: (event: YouTubeErrorEvent) => void;
      };
    },
  ) => YouTubePlayer;
};

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

type AudioContextValue = {
  started: boolean;
  muted: boolean;
  volume: number;
  startMusic: () => Promise<void>;
  toggleMute: () => void;
  setVolume: (value: number) => void;
  playEffect: (src: string, duck?: boolean) => void;
};

const BirthdayAudioContext = createContext<AudioContextValue | null>(null);

function loadYouTubeApi(onReady: () => void) {
  if (window.YT?.Player) {
    onReady();
    return;
  }

  const previousReady = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    previousReady?.();
    onReady();
  };

  if (!document.querySelector('script[data-birthday-youtube-api="true"]')) {
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.dataset.birthdayYoutubeApi = 'true';
    document.head.appendChild(script);
  }
}

export function BirthdayAudioProvider({ children }: { children: React.ReactNode }) {
  const localBackgroundRef = useRef<HTMLAudioElement | null>(null);
  const youtubePlayerRef = useRef<YouTubePlayer | null>(null);
  const youtubeReadyRef = useRef(false);
  const youtubeFailedRef = useRef(false);
  const startRequestedRef = useRef(false);
  const duckTimer = useRef<number | undefined>(undefined);
  const startedRef = useRef(sessionStorage.getItem('birthday-audio-started') === 'true');
  const mutedRef = useRef(false);
  const volumeRef = useRef(birthdayConfig.audio.backgroundVolume);

  const [started, setStarted] = useState(() => sessionStorage.getItem('birthday-audio-started') === 'true');
  const [muted, setMuted] = useState(false);
  const [volume, setVolumeState] = useState(birthdayConfig.audio.backgroundVolume);

  const youtubeVideoId = birthdayConfig.audio.preferYouTubeTrack
    ? birthdayConfig.audio.youtubeVideoId.trim()
    : '';
  const useYouTube = youtubeVideoId.length > 0;

  const playLocalFallback = useCallback(() => {
    const audio = localBackgroundRef.current;
    if (!audio || !startedRef.current || mutedRef.current) return;
    audio.volume = volumeRef.current;
    void audio.play().catch(() => undefined);
  }, []);

  useEffect(() => {
    let disposed = false;
    let youtubeLoadTimeout: number | undefined;
    const localAudio = new Audio(assetUrl(birthdayConfig.audio.backgroundTrack));
    localAudio.loop = birthdayConfig.audio.loopBackgroundMusic;
    localAudio.preload = 'metadata';
    localAudio.volume = volumeRef.current;
    localBackgroundRef.current = localAudio;

    const playRequestedSource = () => {
      if (!startedRef.current || mutedRef.current) return;

      if (useYouTube && youtubeReadyRef.current && youtubePlayerRef.current && !youtubeFailedRef.current) {
        youtubePlayerRef.current.unMute();
        youtubePlayerRef.current.setVolume(Math.round(volumeRef.current * 100));
        youtubePlayerRef.current.playVideo();
        localAudio.pause();
      } else if (!useYouTube || youtubeFailedRef.current) {
        playLocalFallback();
      }
    };

    if (useYouTube) {
      loadYouTubeApi(() => {
        if (disposed || !window.YT?.Player || youtubePlayerRef.current) return;

        youtubePlayerRef.current = new window.YT.Player('birthday-youtube-player', {
          width: 200,
          height: 200,
          videoId: youtubeVideoId,
          playerVars: {
            autoplay: 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            loop: 1,
            playlist: youtubeVideoId,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
          },
          events: {
            onReady: () => {
              youtubeReadyRef.current = true;
              youtubePlayerRef.current?.setVolume(Math.round(volumeRef.current * 100));
              if (mutedRef.current) youtubePlayerRef.current?.mute();
              if (startRequestedRef.current || startedRef.current) playRequestedSource();
            },
            onStateChange: (event) => {
              if (event.data === 0 && startedRef.current) {
                youtubePlayerRef.current?.seekTo(0, true);
                youtubePlayerRef.current?.playVideo();
              }
            },
            onError: () => {
              youtubeFailedRef.current = true;
              playLocalFallback();
            },
          },
        });
      });

      youtubeLoadTimeout = window.setTimeout(() => {
        if (!disposed && !youtubeReadyRef.current) {
          youtubeFailedRef.current = true;
          playLocalFallback();
        }
      }, 8000);
    }

    const handleVisibility = () => {
      if (!document.hidden) playRequestedSource();
    };

    const handlePageShow = () => playRequestedSource();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);

    if (startedRef.current) {
      startRequestedRef.current = true;
      window.setTimeout(playRequestedSource, 250);
    }

    return () => {
      disposed = true;
      window.clearTimeout(youtubeLoadTimeout);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
      window.clearTimeout(duckTimer.current);
      localAudio.pause();
      youtubePlayerRef.current?.destroy();
      youtubePlayerRef.current = null;
      localBackgroundRef.current = null;
    };
  }, [playLocalFallback, useYouTube, youtubeVideoId]);

  useEffect(() => {
    mutedRef.current = muted;
    volumeRef.current = volume;

    const localAudio = localBackgroundRef.current;
    if (localAudio) {
      localAudio.muted = muted;
      localAudio.volume = volume;
    }

    const youtubePlayer = youtubePlayerRef.current;
    if (youtubePlayer) {
      youtubePlayer.setVolume(Math.round(volume * 100));
      if (muted) youtubePlayer.mute();
      else {
        youtubePlayer.unMute();
        if (startedRef.current) youtubePlayer.playVideo();
      }
    }
  }, [muted, volume]);

  const startMusic = useCallback(async () => {
    setStarted(true);
    setMuted(false);
    startedRef.current = true;
    mutedRef.current = false;
    startRequestedRef.current = true;
    sessionStorage.setItem('birthday-audio-started', 'true');

    if (useYouTube && youtubeReadyRef.current && youtubePlayerRef.current && !youtubeFailedRef.current) {
      youtubePlayerRef.current.unMute();
      youtubePlayerRef.current.setVolume(Math.round(volumeRef.current * 100));
      youtubePlayerRef.current.playVideo();
      localBackgroundRef.current?.pause();
      return;
    }

    if (!useYouTube || youtubeFailedRef.current) {
      playLocalFallback();
    }
  }, [playLocalFallback, useYouTube]);

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      mutedRef.current = next;

      if (next) {
        localBackgroundRef.current?.pause();
        youtubePlayerRef.current?.mute();
      } else if (startedRef.current) {
        if (useYouTube && youtubeReadyRef.current && youtubePlayerRef.current && !youtubeFailedRef.current) {
          youtubePlayerRef.current.unMute();
          youtubePlayerRef.current.playVideo();
        } else {
          playLocalFallback();
        }
      }

      return next;
    });
  }, [playLocalFallback, useYouTube]);

  const setVolume = useCallback((value: number) => {
    const next = Math.max(0, Math.min(1, value));
    volumeRef.current = next;
    setVolumeState(next);
  }, []);

  const playEffect = useCallback(
    (src: string, duck = true) => {
      const effect = new Audio(assetUrl(src));
      effect.volume = 0.85;

      if (duck && !mutedRef.current) {
        if (localBackgroundRef.current) {
          localBackgroundRef.current.volume = Math.min(volumeRef.current, 0.12);
        }
        youtubePlayerRef.current?.setVolume(12);

        window.clearTimeout(duckTimer.current);
        duckTimer.current = window.setTimeout(() => {
          if (localBackgroundRef.current) localBackgroundRef.current.volume = volumeRef.current;
          youtubePlayerRef.current?.setVolume(Math.round(volumeRef.current * 100));
        }, 2600);
      }

      void effect.play().catch(() => undefined);
    },
    [],
  );

  const value = useMemo(
    () => ({ started, muted, volume, startMusic, toggleMute, setVolume, playEffect }),
    [started, muted, volume, startMusic, toggleMute, setVolume, playEffect],
  );

  return (
    <BirthdayAudioContext.Provider value={value}>
      <div id="birthday-youtube-player" className="youtube-audio-player" aria-hidden="true" />
      {children}
    </BirthdayAudioContext.Provider>
  );
}

export function useBirthdayAudio() {
  const context = useContext<AudioContextValue | null>(BirthdayAudioContext);
  if (!context) throw new Error('useBirthdayAudio must be used inside BirthdayAudioProvider');
  return context;
}